import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, asyncScheduler, switchMap } from 'rxjs';
import { HubStatus } from '@app/constants';
import { FeedbackService, LoadingService, ReportService } from '@app/core/services';
import { ErrorMessages } from '@app/enums';
import { isNil } from '@app/utils';
import { IDepositListRequestModel, IDepositModel } from '../models';
import { SocketMonitorService } from './socket-monitor.service';
import { LinqSocketsService } from '../sockets/services';
import { HubData } from '../sockets/shared/interfaces';

@Injectable({ providedIn: 'root' })
export class DepositsService {
	endpointBase = '/api/deposits';

	constructor(
		private readonly http: HttpClient,
		protected loadingService: LoadingService,
		private readonly reportService: ReportService,
		private readonly feedbackService: FeedbackService,
		private readonly linqSocketsService: LinqSocketsService,
		private readonly socketMonitorService: SocketMonitorService,
	) {}

	getDepositsList(requestModel: IDepositListRequestModel): Observable<any> {
		return this.http.post(`${this.endpointBase}/deposits-list`, requestModel);
	}

	getDepositLinesList(bankId: number, depositNumber?: number): Observable<any> {
		let params = new HttpParams();
		params = params.set('bankId', bankId.toString());
		if (!isNil(depositNumber)) {
			params = params.set('depositNumber', depositNumber?.toString() || '');
		}
		return this.http.get(`${this.endpointBase}/deposit-lines-list`, { params });
	}

	getDeposit(bankId: number, depositNumber: number): Observable<any> {
		return this.http.get(`${this.endpointBase}`, {
			params: {
				bankId: bankId.toString(),
				depositNumber: depositNumber.toString(),
			},
		});
	}

	getNextAvailableDepositNumber(bankId: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/next-available-deposit-number`, { params: { bankId: bankId.toString() } });
	}

	saveDeposit(depositModel: IDepositModel, postDeposit: boolean): Observable<any> {
		return this.http.post(`${this.endpointBase}`, { DepositModel: depositModel, PostDeposit: postDeposit });
	}

	voidDeposit(bankId: number, depositNumber: number, effectiveDate: Date, reinstate: boolean): Observable<any> {
		return this.http.post(`${this.endpointBase}/void-deposit`, {
			BankId: bankId,
			DepositNumber: depositNumber,
			EffectiveDate: effectiveDate,
			ReinstateDeposit: reinstate,
		});
	}

	deleteDeposit(bankId: number, depositNumber: number): Observable<any> {
		return this.http.delete(`${this.endpointBase}`, {
			params: {
				bankId: bankId.toString(),
				depositNumber: depositNumber.toString(),
			},
		});
	}

	queuePrintDeposit(depositModel: IDepositModel): Observable<any> {
		return this.http.post(`${this.endpointBase}/queue-print-deposit`, depositModel);
	}

	/**
	 * showDepositInPrintDialog() uses the socket monitor service to show the deposit in the print dialog
	 * once data is received from the socket, it calls the report service to show the print dialog
	 * @param depositModel The deposit model to print
	 * @param destroy$ An observable that signals when to stop monitoring the socket
	 */
	public showDepositInPrintDialog(depositModel: IDepositModel, destroy$: Observable<any>): Promise<void> {
		return new Promise((resolve) => {
			this.loadingService.show();
			this.queuePrintDeposit(depositModel)
				.pipe(switchMap((processId: string) => this.socketMonitorService.monitorSocketProcess(processId, destroy$)))
				.subscribe((result: HubData) => {
					if (result.Status === HubStatus.Success) {
						const { BlobData } = result.Data;
						if (isNil(BlobData)) {
							this.feedbackService.error(ErrorMessages.ErrorContactAdministrator);
						} else {
							this.reportService.showPrintDialog(BlobData.Data, BlobData.ContentType);
						}

						asyncScheduler.schedule(() => {
							this.linqSocketsService.removeProcess(result.Id);
							if (this.loadingService.loading$.getValue() === true) {
								this.loadingService.hide();
							}
						}, 2000);
					} else if (this.loadingService.loading$.getValue() === true) {
						this.loadingService.hide();
					}

					if (result?.Status && result.Status === HubStatus.Error) {
						this.feedbackService.error(ErrorMessages.ErrorContactAdministrator);
					}
					resolve();
				});
		});
	}

	/**
	 * Fetches a deposit using unique keys to group receipts from the api and shows the print slip dialog
	 * @param bankId
	 * @param depositNumber
	 * @param destroy$
	 * @returns
	 */
	public getDepositAndPrintSlip(bankId: number, depositNumber: number, destroy$: Observable<any>): Promise<IDepositModel> {
		return new Promise<IDepositModel>((resolve) => {
			this.getDeposit(bankId, depositNumber).subscribe((deposit) => {
				this.showDepositInPrintDialog(deposit, destroy$).then(() => {
					resolve(deposit);
				});
			});
		});
	}
}
